import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import QRList from "./QRList";
import useQRCodeService from "../../../../services/qrCodeService";
import useFeedbackFormService from "../../../../services/feedbackFormService";
import CreateQRModal from "./CreateQRModal";
import { useAuth } from "../../../../context/authContext";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";

const DashboardQR = () => {
  const [qrCodes, setQRCodes] = useState([]);
  const [loadingQR, setLoadingQR] = useState(false);
  const [feedbackForms, setFeedbackForms] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [name, setName] = useState("");
  const [selectedFormId, setSelectedFormId] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [logoFile, setLogoFile] = useState(null);
  const { user, fetchUser } = useAuth();
  const { getQRCodes, createQRCode, editQRCode, deleteQRCode } =
    useQRCodeService();
  const { getFeedbackForms } = useFeedbackFormService();
  const { language } = useLanguage();
  const text = dashboardContent[language].qr;

  const { reportUsage, reportLimits } = user;
  const limitReached =
    typeof reportUsage.qrCodeCreation === "number" &&
    typeof reportLimits.qrCodeCreation === "number" &&
    reportUsage.qrCodeCreation >= reportLimits.qrCodeCreation;

  useEffect(() => {
    // Prüfe, ob openModal im state true ist
    if (location.state?.openModal) {
      setIsModalVisible(true);
    }
  }, [location.state]);
  // QR-Codes laden
  useEffect(() => {
    const fetchQRCodes = async () => {
      setLoadingQR(true);
      try {
        const qrCodeList = await getQRCodes();
        console.log(qrCodeList);
        setQRCodes(qrCodeList.qrCodes);
      } catch (error) {
        console.error("Fehler beim Laden der QR-Codes:", error);
      }
      setLoadingQR(false);
    };

    fetchQRCodes();
  }, []);

  // Feedback-Formulare laden
  useEffect(() => {
    const fetchFeedbackForms = async () => {
      try {
        const forms = await getFeedbackForms();
        setFeedbackForms(forms);
      } catch (error) {
        console.error("Fehler beim Laden der Feedback-Formulare:", error);
      }
    };

    fetchFeedbackForms();
  }, []);

  const handleLogoChange = (input) => {
    if (input && input.target && input.target.files) {
      setLogoFile(input.target.files[0]);
    } else if (input instanceof File) {
      setLogoFile(input);
    }
  };

  const handleCreateQRCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    const id = feedbackForms.find((form) => form.name === selectedFormId)?._id;
    try {
      const data = `${process.env.REACT_APP_FRONTEND_URL}/feedbackform/${id}`;
      const formData = new FormData();
      formData.append("name", name);
      formData.append("data", data);
      formData.append("id", id);
      if (logoFile) {
        formData.append("logo", logoFile);
      }

      const newQRCode = await createQRCode(formData);
      await fetchUser();
      if (newQRCode) {
        if (Array.isArray(qrCodes)) {
          setQRCodes([...qrCodes, newQRCode]);
        } else {
          setQRCodes([newQRCode]);
        }
        setIsModalVisible(false);
        setName("");
        setSelectedFormId("");
      }
    } catch (error) {
      console.error("Fehler beim Erstellen des QR-Codes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditQRCode = async (id, updates) => {
    setLoading(true);
    try {
      const updatedQRCode = await editQRCode(id, updates);
      if (updatedQRCode) {
        setQRCodes(
          qrCodes.map((qrCode) => (qrCode._id === id ? updatedQRCode : qrCode))
        );
      }
    } catch (error) {
      console.error("Fehler beim Bearbeiten des QR-Codes:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteQRCode = async (id) => {
    setLoading(true); // Ladezustand aktivieren
    try {
      // API-Aufruf zum Löschen
      const isDeleted = await deleteQRCode(id);
      await fetchUser();

      if (isDeleted) {
        // Nur nach erfolgreichem Löschen die Liste aktualisieren
        setQRCodes(qrCodes.filter((qrCode) => qrCode._id !== id._id));
        console.log(`QR-Code mit ID ${id._id} erfolgreich gelöscht.`);
      } else {
        throw new Error("Fehler beim Löschen des QR-Codes.");
      }
    } catch (error) {
      console.error("Fehler beim Löschen des QR-Codes:", error);
    } finally {
      setLoading(false); // Ladezustand deaktivieren
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">{text.title}</h2>
        <button
          className={`px-4 py-2 rounded text-white transition ${
            limitReached
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600"
          }`}
          onClick={() => !limitReached && setIsModalVisible(true)}
          disabled={limitReached}
        >
          {text.createButton}
        </button>
      </div>

      <QRList
        qrCodes={qrCodes}
        onEdit={handleEditQRCode}
        onDelete={handleDeleteQRCode}
        loadingQR={loadingQR}
      />

      <CreateQRModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleCreateQRCode}
        name={name}
        onNameChange={(e) => setName(e.target.value)}
        selectedFormId={selectedFormId}
        onSelectedFormIdChange={(e) => setSelectedFormId(e.target.value)}
        feedbackForms={feedbackForms}
        loading={loading}
        onLogoChange={handleLogoChange}
      />
    </div>
  );
};

export default DashboardQR;
